import React from "react";
import { useTranslation } from "react-i18next";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const cx = (...xs) => xs.filter(Boolean).join(" ");

export default function ProfileStatsStrip({
    stats,
    active = "posts",
    onOpen,
    /**
     * "strip": franja bajo el header (móvil), con borde superior.
     * "inline": misma fila horizontal que strip, embebida a la derecha en desktop (sin borde extra).
     */
    layout = "strip",
    /** En layout strip, línea superior bajo el banner (perfil móvil suele ir en false). */
    stripTopBorder = true,
    className,
    loading = false,
}) {
    const { t } = useTranslation();
    const fmt = React.useMemo(() => new Intl.NumberFormat(), []);

    const tabs = React.useMemo(
        () => [
            { key: "pets", label: t("profile.stats.pets"), value: stats?.pets ?? 0 },
            { key: "posts", label: t("profile.stats.posts"), value: stats?.posts ?? 0 },
            { key: "followers", label: t("profile.stats.followers"), value: stats?.followers ?? 0 },
            { key: "following", label: t("profile.stats.following"), value: stats?.following ?? 0 },
        ],
        [t, stats]
    );

    const isInline = layout === "inline";

    return (
        <div
            className={cx(
                "w-full",
                isInline ? "border-none" : (stripTopBorder ? "border-t border-gray-100 dark:border-white/5" : "border-none"),
                className
            )}
        >
            <div
                role="tablist"
                aria-label="Profile stats"
                className={cx(
                    "flex items-end px-0 sm:px-0 pb-2",
                    isInline ? "justify-between sm:justify-end gap-x-2 sm:gap-x-6" : "justify-start gap-x-4 sm:gap-x-6"
                )}
            >
                {loading ? (
                    <div className="flex items-end gap-x-4 sm:gap-x-6 w-full px-0">
                        {[...Array(4)].map((_, i) => (
                            <Skeleton key={i} width={64} height={36} />
                        ))}
                    </div>
                ) : tabs.map((row) => {
                    const isActive = row.key === active;

                    return (
                        <button
                            key={row.key}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => onOpen?.(row.key)}
                            className="flex flex-col items-center justify-end min-w-0 group rounded-md h-10 flex-initial gap-1"
                        >
                            <span
                                className={cx(
                                    "text-base sm:text-[16px] font-bold tracking-tight tabular-nums leading-none",
                                    isActive
                                        ? "text-primary"
                                        : "text-[#191919] dark:text-[#f3f2ef]"
                                )}
                            >
                                {fmt.format(row.value)}
                            </span>
                            <span
                                className={cx(
                                    "text-[13px] lowercase truncate text-center leading-none",
                                    isActive
                                        ? "text-primary"
                                        : "text-subtle-light dark:text-subtle-dark"
                                )}
                            >
                                {row.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}